import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { Observer, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { Me } from './auth/interface/auth.interface';
import { AuthService } from './auth/service/auth.service';
import { StoreState } from './store';
import { setLoggedUserData } from './store/login/actions';
import { normalize } from './utils/normalizeString';
import { ofuscate } from './utils/obfuscation';
import { setPageErrorData } from './views/auth-failure/duck/actions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'DLMS - Driver License Management System';
  isIframe = false;
  lastEvent = '';
  loading = true;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private broadcastService: MsalBroadcastService,
    private authService: MsalService,
    private dlsmAuthService: AuthService,
    private store: Store<StoreState>,
    private router: Router,
  ) {

  }

  setUserDataObserver: Observer<Me> = {
    next: (me) => {
      const { roles, privileges, ...otherUserData } = me;
      let error = 'A.1';

      if (privileges) {
        error = 'A.2';
        if (privileges.length > 0) {
          error = 'A.3';
          const ofuscatedRolesAndPrivileges = ofuscate({ roles, privileges });

          localStorage.setItem('session', ofuscatedRolesAndPrivileges);
          this.store.dispatch(setLoggedUserData(otherUserData));

          const [website, ...url] = window.location.href.split('//')[1].split('/');

          this.store.select('pageError').subscribe({
            next: (pageConfig) => {
              error = 'A.4';
              if (pageConfig.errorType !== 'Mulesoft' && url[0].includes('auth-failure')) {
                error = 'A.4.1';
                this.router.navigateByUrl('/');
              }
            },
            error: (e) => {
              error = 'A.5';
            },
          });
          this.loading = false;
          return;
        }
      }
      error = 'A.6';
      this.store.dispatch(setPageErrorData({
        errorType: 'DLMS',
        name: `${error}: No tiene los permisos necesarios para ingresar.`,
        message: `El rol asignado al usuario ${this.getEmail()} no tiene permisos para entrar a esta sección. Por favor contacte con su superior.`,
        buttonAction: 'CHANGE_USER',
      }));
      this.router.navigateByUrl('/auth-failure');
      this.loading = false;
    },
    error: (error) => {
      if (this.lastEvent !== 'handleRedirectStart') {
        this.store.dispatch(setPageErrorData({
          errorType: 'DLMS',
          name: 'A.7: No tiene los permisos necesarios para ingresar.',
          message: `El rol asignado al usuario ${this.getEmail()} no tiene permisos para entrar a esta sección. Por favor contacte con su superior.`,
          buttonAction: 'CHANGE_USER',
        }));
        this.router.navigateByUrl('/auth-failure');
        this.loading = false;
      }
    },
    complete: () => {
    },
  };

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;

    this.authService.handleRedirectObservable().subscribe(({
      next: (authenticationResult) => {
        if (authenticationResult) {
          const params = {
            name: authenticationResult.account?.name,
            email: authenticationResult.account?.username.includes('@') ?
              authenticationResult.account?.username :
              (authenticationResult.idTokenClaims as any).preferred_username,
          };
          this.dlsmAuthService.addToLogLoginSuccessfully(params);
        }
      },
    }));

    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None || status === InteractionStatus.HandleRedirect),
        takeUntil(this._destroying$),
        switchMap(() => this.dlsmAuthService.getMe()),
      )
      .subscribe(this.setUserDataObserver);

    this.authService.instance.addEventCallback((eventDescription: any) => {
      const event = eventDescription.eventType.split('msal:')[1];
      this.lastEvent = event;
      // this.authService.instance.
      if (!(!this.loading && (event === 'acquireTokenStart' || event === 'acquireTokenSuccess'))) {
        console.warn('msal: ', event);
      }

      if (normalize(event) === normalize('acquireTokenFailure')) {
        this.dlsmAuthService.logout();
      }
      if (event === 'loginSuccess') {
        this.dlsmAuthService.getMe().subscribe(this.setUserDataObserver);
      }
    });
  }

  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);

    }
    else {
      this.authService.loginRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  getEmail(): string {
    const currentAccount = this.authService.instance.getAllAccounts()[0];
    return currentAccount.username;
  }
}
